/* global $, year */
import * as THREE from 'three'
import _template from 'lodash/template'
import flatten from 'lodash/flatten'
import filter from 'lodash/filter'
import findIndex from 'lodash/findIndex'

import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import StadiumPaths from 'bam-node-evp/src/3d/Utils/StadiumOutfields'
import TransformSVGPath2 from './transform-svg'

import GL from './util/gl'
import model from './model'
import Loop from './animation_loop'

import UI from './ui'

import metricsUtil from './util/metrics'
import { toFence } from './fence-helpers'
import getFieldComponents from './field-components'

// NB: below used to avoid GPU memory pipeline crash on iOS
//     (https://discourse.threejs.org/t/textures-in-gltf-sometimes-display-black-but-only-on-ios/30520/26)
window.createImageBitmap = undefined

const feetPerMeter = 3.28084
const DEFAULT_ASSET_BASE =
    // 'https://fv-assets.mlb.com/v/9fe0889df036e6441d36bf22cbeaca53868464c0'
    'https://fv-assets.mlb.com/v/ab8a372888f9134ee90e8acff64b5c0666ec401e'
// https://fv-assets.mlb.com/v/c78060bee8626fbc4cd8087269bb3fe51c2d0c45/models/ballparks/Stadium_BOS.glb

const canvasEl = document.querySelector('article.hit-spread canvas')
// gl = GL.withOrthographicCamera(canvasEl),
const gl = GL.withPerspectiveCamera(canvasEl, {
    alpha: true,
    antialias: true,
})
const controls = new OrbitControls(gl.camera, gl.renderer.domElement)

const loop = new Loop(gl, controls)
const ui = new UI(canvasEl, gl, loop, controls)

if (Number(year) === 2017) {
    gl.camera.position.set(0, -60, 20)
} else if (Number(year) === 2023 || String(year).slice(0, 4) === '2023') {
    gl.camera.position.set(0, -100, 80)
} else {
    gl.camera.position.set(0, -180, 140)
}

/* temp: generic orbit controls */
controls.addEventListener('change', gl.tick.bind(gl))
// TODO    when damping true, might need to ensure the loop runs continuously, or at least while mouse down
// controls.enableDamping = true;
controls.enableZoom = true
// controls.target.set(0, 300, 0);
controls.target.set(0, 180, 0)
controls.maxPolarAngle = 0.49 * Math.PI
controls.autoRotateSpeed = 2.5
controls.update()
controls.minDistance = 200
controls.maxDistance = 650
controls.autoRotate = false

/* add utils */
/* var axis = new THREE.AxisHelper(20);
     axis.position.y = 100;
     axis.position.z = 1;
     gl.add(axis); */

/* var grid = new THREE.GridHelper(500, 10);
     grid.rotation.x = 0.5 * Math.PI;
     grid.position.y = 100;
     grid.position.z = -5;
     grid.material.transparent = true;
     grid.material.opacity = 0.1;
     gl.add(grid); */

/* add lights */
const ambient = new THREE.AmbientLight(0x666666, 2)
gl.add(ambient)

const topdown = new THREE.DirectionalLight(0x6d6b58, 1)
topdown.position.set(0, 0, 1)
gl.add(topdown)

// const bottomup = new THREE.DirectionalLight(0x666666, 0.6)
// bottomup.position.set(0, 0, -1)
// gl.add(bottomup)

const dirLight = new THREE.DirectionalLight(0x666666)
dirLight.position.set(9, 30, 30)
dirLight.castShadow = true
dirLight.shadow.camera.top = 6
dirLight.shadow.camera.bottom = -6
dirLight.shadow.camera.left = -6
dirLight.shadow.camera.right = 6
dirLight.shadow.camera.near = 0.3
dirLight.shadow.camera.far = 100
gl.add(dirLight)

const hemiLight = new THREE.HemisphereLight(0x2c56c0, 0x253e25, 0.5)
hemiLight.position.set(0, 120, 0)
gl.add(hemiLight)

/* var spot1 = new THREE.SpotLight({ color: 0xff0000 }, 0.5);
     spot1.penumbra = 0.1;
     spot1.distance = 300;
     spot1.angle = Math.PI / 6;
     spot1.position.set(0, 200, 200);
     spot1.target.position.y = 300;

     gl.add(spot1);
     gl.add(spot1.target); */

/* add venue obj */
// Field.fromID('4169').then((obj) => {
//     obj.name = 'field'
//     obj.scale.setScalar(FIELD_SCALE)
//     gl = gl.add(obj)
//     gl.tick()
// })

const dracoLoader = new DRACOLoader()
const gltfLoader = new GLTFLoader()
gltfLoader.setDRACOLoader(dracoLoader)

dracoLoader.setDecoderPath(`${DEFAULT_ASSET_BASE}/libs/draco/`)

function getVenueId(year) {
    switch (year) {
        //  'SDP',
        case 2016:
            return 2680
        //  'MIA',
        case 2017:
            return 4169
        //  'WSH',
        case 2018:
            return 3309
        //  'CLE',
        case 2019:
            return 5
        //  'COL',
        case 2021:
            return 19
        //  'LAD',
        case 2022:
            return 22
        // Seattle
        case 2023:
        case 20230:
        case 20231:
        case 20232:
            return 680
        // Texas
        case 2024:
        case 20240:
        case 20241:
        case 20242:
        case 20243:
        case 20244:
            return 5325
        case 2025:
        case 20250:
        case 20251:
        default:
            return 4705
    }
}

function loadStadium() {
    const stadiumMap = {
        2025: '4705_ATL',
        20250: '4705_ATL',
        20251: '4705_ATL',
        20252: '4705_ATL',
        20253: '4705_ATL',
        20254: '4705_ATL',
        2024: '5325_TEX',
        20244: '5325_TEX',
        20243: '5325_TEX',
        20242: '5325_TEX',
        20241: '5325_TEX',
        20240: '5325_TEX',
        2023: '680_SEA',
        20230: '680_SEA',
        20231: '680_SEA',
        20232: '680_SEA',
        2022: '22_LAD',
        2021: '19_COL',
        2019: '5_CLE',
        2018: '3309_WSH',
        2017: '4169_MIA',
        2016: '2680_SDP',
    }

    const stadiumAbbrev = stadiumMap[year] || 'SEA'
    return new Promise((resolve, reject) => {
        gltfLoader.load(
            `${DEFAULT_ASSET_BASE}/models/ballparks/${stadiumAbbrev}.glb`,
            resolve,
            () => {},
            reject
        )
    })
}

function loadData() {
    return fetch(`/derby-data?year=${year}`).then((res) => res.json())
}

// resize scene on window resize
window.addEventListener('resize', gl.resize.bind(gl))

$(document).ready(() => {
    $('#infoOptions').click(() => {
        if ($('#options:visible').length) $('#options').hide()
        else $('#options').show()
    })

    const $menu = document.getElementById('hr-derby-menu')
    let visible = false

    document
        .getElementById('hr-derby-current-year')
        .addEventListener('click', (e) => {
            if (visible) {
                $menu.style.display = 'none'
            } else {
                $menu.style.display = 'block'
            }
            visible = !visible
            e.stopPropagation()
        })
    document.addEventListener('click', () => {
        $menu.style.display = 'none'
        visible = false
    })

    // simId = '87';

    let isDerbyOver = false
    // const simSpeed = 20
    // const simOffset = 250

    let prevHits = []

    const stadiumPromise = loadStadium()
    const dataPromise = loadData()

    let stadium
    const simpleStadium = getSimpleStadium(year)

    Promise.all([stadiumPromise, dataPromise])
        .then(([stadiumGLTF, data]) => {
            // Add Stadium
            stadium = setupThreeDStadium(stadiumGLTF)

            gl.scene.add(stadium)
            gl.tick()

            applyStadiumUI(gl, stadium, simpleStadium)

            if (data.players) {
                setupPlayers(data.players)
            }

            if (!data.players || data.status.state === 'Preview') {
                setTimeout(poll2, 30000)
                return
            }

            // eslint-disable-next-line no-console
            console.log('********** derby-data hits:', data)
            if (data.status.state === 'Final') {
                // eslint-disable-next-line no-console
                console.log('********** stop polling **********')
                isDerbyOver = true
            }

            if (data.hrs.length > 0) {
                drawHits(data, isDerbyOver)
                $('#metaTable').trigger('update')
                // $('#metaTable').tablesorter()
            }

            if (!isDerbyOver) {
                setTimeout(poll2, 5000)
            }
        })
        .catch((err) => {
            console.error(err)
        })

    async function poll2() {
        const data = await loadData()

        if (!data.players || data.status.state === 'Preview') {
            setTimeout(poll2, 30000)
            return
        }

        // eslint-disable-next-line no-console
        console.log('********** derby-data hits:', data)
        if (data.status.state === 'Final') {
            // eslint-disable-next-line no-console
            console.log('********** stop polling **********')
            isDerbyOver = true
        }

        if (data.hrs.length > 0) {
            drawHits(data, isDerbyOver)
            $('#metaTable').trigger('update')
            // $('#metaTable').tablesorter()
        }

        if (!isDerbyOver) {
            setTimeout(poll2, 5000)
        }
    }

    // var prevHits = [];

    // if it ever becomes critical, could save a little time by borrowing already calculated coords from prevHits
    function drawHits(derbyData, isFinal) {
        const hits = derbyData.hrs

        // console.log('prevHits:', prevHits.length, ', hits:', hits.length);

        generatePaths(derbyData)

        hits.forEach(metricsUtil.addLaunchAngle)
        hits.forEach(metricsUtil.addMaxHeight)

        // console.log(convert);
        ui.update(model(hits, prevHits, derbyData), isFinal)
        ui.setTable()

        prevHits = hits
    }
})

function setupPlayers(players = []) {
    const templateStr = `
        <div class="player" data-id="<%= id %>">
                <img src="https://content.mlb.com/images/headshots/current/60x60/<%= id %>@2x.png" id="<%= id %>"
                            class="circular--square mugshot"/>
                <div class="name">
                    <%= boxscoreName %>
                </div>
                <div class="rounds" id="p_<%= id %>_selector">
                </div>
                <div class="tip" id="p_<%= id %>_tip"></div>
                <div class="live_badge">Live</div>
        </div>
    `

    const playerTemplate = _template(templateStr)

    const $mugshotContainer = document.getElementById('playerMugshots')
    players.forEach((p) => {
        $mugshotContainer.innerHTML += playerTemplate(p)
    })
}

const useGivenInterval = false
const heightThreshold = 0

function generatePaths(derbyData) {
    derbyData.hrs.forEach((hit) => {
        if (hit.result) {
            const pfx = pf(hit.result.hit.polynomialX)
            const pfy = pf(hit.result.hit.polynomialY)
            const pfz = pf(hit.result.hit.polynomialZ)
            // var interval = hitSegment.trajectoryData.validTimeInterval;
            const start = useGivenInterval ? hit.result.hit.interval[0] : 0
            const end = useGivenInterval ? hit.result.hit.interval[1] : 7
            const step = 0.05
            const pathLen = Math.ceil((end - start) / step)
            let path = new Float32Array(3 * pathLen)

            let points = []

            for (let i = 0; i < pathLen; i++) {
                const p = path.subarray(3 * i, 3 * i + 3)
                const t = start + i * step
                p[0] = pfx(t)
                p[1] = pfy(t)
                p[2] = pfz(t)

                points = [...points, [p[0], p[1], p[2]]]
            }

            // let points = groupBy(path, (n, i) => Math.floor(i / 3))

            if (!useGivenInterval) {
                points = clipHeadToPlate(points)
                points = clipTailToHeightThreshold(points, heightThreshold)
            }

            path = flatten(points)

            // TODO    can we infer the interval from clipHeadToPlate and clipTailToHeightThreshold clip indexes?
            // could be used to calculate hang time

            // eslint-disable-next-line no-param-reassign
            hit.result.hit.path = path
        }
    })
}

/**
 * Filters out points behind the tip of the plate
 * @param points
 */
function clipHeadToPlate(points) {
    return filter(points, (p) => p[1] >= 0)
}

/* function saveSTL( scene, name ){
        var exporter = new THREE.STLExporter();
        var stlString = exporter.parse( scene );

        //var blob = new Blob([stlString], {type: 'text/plain'});
        //console.log(stlString);
        //saveAs(blob, name + '.stl');
        $.ajax({
            type: 'POST',
            url: 'http://localhost:4000/stl',
            data: { data: stlString },
            succes: function() {
              console.log('done!');
            }
          })
      } */

/**
 * Limits to points up to first point that falls below given height threshold
 * @param points
 * @param height
 */
function clipTailToHeightThreshold(points, height) {
    const halfPoints = 0.5 * points.length

    const i = findIndex(points, (p, j) => j >= halfPoints && p[2] < height)

    if (i !== -1) {
        return points.slice(0, i)
    }

    return points
}

function pf(coef) {
    return function subFn(val) {
        let sum = 0
        if (coef) {
            for (let i = coef.length - 1; i >= 0; i--) {
                const c = coef[i]
                sum += c * val ** i
            }
        }
        return sum
    }
}

function getStadium(id) {
    const { outfield, hasTransform } = StadiumPaths[id] || {}
    const fieldPath = TransformSVGPath2(outfield, hasTransform)

    // let fieldPath = transformSVGPath(stadiums.getStadiums(id))
    // if (id === 3289) {
    //     fieldPath = TransformSVGPath2(stadiums.getStadiums(id), true)
    // }

    const extrudeSettings = {
        depth: 1,
        bevelEnabled: true,
        bevelSegments: 2,
        steps: 2,
        bevelSize: 1,
        bevelThickness: 1,
    }

    const geometry = new THREE.ExtrudeGeometry(fieldPath, extrudeSettings)

    const material = new THREE.MeshBasicMaterial({
        color: 0xbbe8e4,
        shininess: 0.25,
        //        specular: '#c2c2c2',
        wireframe: false,
        flatShading: false,
        wireframeLinewidth: 0.4,
        transparent: true,
        opacity: 1,
    })

    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = `venue_${id}`

    return mesh
}

function getInfield(id) {
    const { infield, hasTransform } = StadiumPaths[id] || {}

    if (infield) {
        const fieldPath = TransformSVGPath2(infield, hasTransform)

        const extrudeSettings = {
            depth: 2,
            bevelEnabled: true,
            bevelSegments: 2,
            steps: 2,
            bevelSize: 1,
            bevelThickness: 1,
        }

        const geometry = new THREE.ExtrudeGeometry(fieldPath, extrudeSettings)

        const material = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            wireframe: false,
            flatShading: false,
            wireframeLinewidth: 0.4,
            transparent: true,
            opacity: 1,
            side: THREE.DoubleSide,
        })
        const mesh = new THREE.Mesh(geometry, material)
        mesh.name = `infield_${id}`
        return mesh
    }

    return null
}

function getFence(id) {
    const { fenceMesh, distance } = toFence({
        venueId: id,
    })
    fenceMesh.name = `default_fence_${id}`

    return { fenceMesh, distance }
}

function getSimpleStadium(year) {
    const group = new THREE.Group()
    group.name = 'simple-stadium'

    const size = 50000
    const divisions = 380

    const gridHelper = new THREE.GridHelper(size, divisions, 0xf0f0f0, 0xf0f0f0)

    // Rotate to flip grid
    gridHelper.geometry.rotateX(Math.PI / 2)
    group.add(gridHelper)

    const venueId = getVenueId(year)
    const mesh = getStadium(venueId)
    const infieldMesh = getInfield(venueId)
    const { fenceMesh, distance } = getFence(venueId)

    group.add(mesh)
    group.add(infieldMesh)
    group.add(fenceMesh)

    if (distance) {
        const fieldComponentsGroup = getFieldComponents({
            lf: distance.lf,
            rf: distance.rf,
        })

        group.add(fieldComponentsGroup)
    }

    return group
}

// eslint-disable-next-line no-shadow
function applyStadiumUI(gl, stadium, simpleStadium) {
    let threeD = true
    const btn = document.getElementById('btnStadium')
    btn.addEventListener('click', () => {
        if (threeD) {
            // eslint-disable-next-line no-param-reassign
            gl.scene.background = new THREE.Color(0xffffff)
            gl.scene.remove(stadium)
            gl.scene.add(simpleStadium)
            btn.innerText = '3D Stadium'
        } else {
            // eslint-disable-next-line no-param-reassign
            gl.scene.background = new THREE.Color(0x34a0e1)
            gl.scene.add(stadium)
            gl.scene.remove(simpleStadium)
            btn.innerText = 'Simple Field'
        }

        threeD = !threeD
        gl.tick()
    })
}

function setupThreeDStadium(stadiumGLTF) {
    gl.scene.background = new THREE.Color(0x34a0e1)

    const group = new THREE.Group()
    group.name = '3d-stadium-group'

    // Add "land"
    const landGeo = new THREE.CircleGeometry(2000, 32)
    const landMaterial = new THREE.MeshBasicMaterial({
        color: 0x031502,
        side: THREE.DoubleSide,
    })
    const land = new THREE.Mesh(landGeo, landMaterial)
    land.position.z = -5 // some of the assets (namely the dugouts) are below ground.
    group.add(land)

    const stadium = stadiumGLTF.scene
    stadium.scale.set(feetPerMeter, feetPerMeter, feetPerMeter)
    stadium.setRotationFromEuler(new THREE.Euler(Math.PI / 2, 0, 0))
    const objs = []
    stadium.traverse((obj) => {
        if (obj.name === 'CameraCollider') {
            // gl.scene.add(obj) // causes an error in the traverse method
            objs.push(obj)
        }
    })

    stadium.remove(...objs)
    stadium.name = '3d-stadium'

    group.add(stadium)

    return group
}
